/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Button } from 'react-bootstrap'
import FsiPicker, { FsiPickerOptions, TreeNode } from './FsiPicker'
import { useServiceContext } from '../../root'
import Modal from '../Modal'

export interface FsiPickerModalProps {
  options?: Partial<FsiPickerOptions>
  title: string
  isOpen: boolean
  onSelect: (fsi: TreeNode | null) => void
  onClose: () => void
  isNodeSelectable?: (node: TreeNode) => boolean
  showAddNewFolderModal?: boolean
  modalClassName?: string
  backdropClassName?: string
  backdropStatic?: boolean // Prevent close on backdrop click if true
}

/**
 * FsiPickerModal Component
 * Modal wrapper for FsiPicker - allows users to select files/folders from the tree
 */
export function FsiPickerModal({
  options,
  title,
  isOpen,
  onSelect,
  onClose,
  isNodeSelectable,
  showAddNewFolderModal,
  modalClassName,
  backdropClassName,
  backdropStatic = false
}: FsiPickerModalProps) {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const [selectedFsi, setSelectedFsi] = useState<TreeNode | null>(null)
  const [show, setShow] = useState(isOpen)

  useEffect(() => {
    setShow(isOpen)
  }, [isOpen])

  const handleClose = () => {
    setShow(false)
    setSelectedFsi(null)
    onClose()
  }

  const handleSelect = () => {
    setShow(false)
    onSelect(selectedFsi)
    setSelectedFsi(null)
  }

  return (
    <div style={{ height: '33rem' }} id="fsi-picker-modal">
      <Modal show={show} className={modalClassName} title={title} handleClose={handleClose}>
        <Modal.Body>
          <FsiPicker
            options={options}
            onSelect={(node) => {
              //@ts-expect-error to match legacy
              setSelectedFsi(node)
            }}
            isNodeSelectable={isNodeSelectable}
            showAddNewFolderModal={showAddNewFolderModal}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="link" onClick={handleClose} data-action="cancel">
            {translate('CANCEL')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShow(false)
              if (selectedFsi) {
                onSelect(selectedFsi)
              } else {
                onClose()
              }
            }}
            data-action="ok"
            className="text-capitalize"
          >
            {translate('OK')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default FsiPickerModal
